import { useCallback, useEffect, useState } from 'react'
import {
  getSocialPostsAdmin,
  updateSocialPostAdmin,
  bulkUpdateSocialPostsAdmin,
  type SocialPostAdminItem,
} from '@/api/adminApi'
import { resolveMediaUrl } from '@/utils/mediaUrl'
import { useSaveFeedback } from '@/hooks/useSaveFeedback'
import styles from './Admin.module.css'

type Filtro = 'all' | 'pendente' | 'aprovado' | 'oculto'

function isImageUrl(url: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp|svg)(\?|#|$)/i.test(url)
}

export function AdminSocialPostsPage() {
  const [list, setList] = useState<SocialPostAdminItem[]>([])
  const [filtro, setFiltro] = useState<Filtro>('all')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [bulkSaving, setBulkSaving] = useState(false)
  const { notifySaved, notifyError } = useSaveFeedback()

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    setSelected(new Set())
    getSocialPostsAdmin(filtro)
      .then(setList)
      .catch((e) => setError(e instanceof Error ? e.message : 'Erro ao carregar'))
      .finally(() => setLoading(false))
  }, [filtro])

  useEffect(() => {
    load()
  }, [load])

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selected.size === list.length) setSelected(new Set())
    else setSelected(new Set(list.map((i) => i.id)))
  }

  const patch = async (
    id: number,
    body: Partial<{ aprovado: boolean; ativo: boolean; destaque: boolean }>
  ) => {
    setSavingId(id)
    try {
      const updated = await updateSocialPostAdmin(id, body)
      setList((prev) =>
        prev.map((i) =>
          i.id === id
            ? {
                ...i,
                aprovado: updated.aprovado,
                ativo: updated.ativo,
                destaque: updated.destaque,
                texto: updated.texto,
                media_url: updated.media_url,
              }
            : i
        )
      )
      notifySaved('Publicação actualizada.')
    } catch (e) {
      notifyError(e, 'Erro ao atualizar')
      setError(e instanceof Error ? e.message : 'Erro ao atualizar')
    } finally {
      setSavingId(null)
    }
  }

  const bulkApprove = async () => {
    if (selected.size === 0) return
    setBulkSaving(true)
    try {
      const updated = await bulkUpdateSocialPostsAdmin([...selected], { aprovado: true })
      notifySaved(`${updated} publicação(ões) aprovada(s).`)
      load()
    } catch (e) {
      notifyError(e, 'Erro na aprovação em massa')
      setError(e instanceof Error ? e.message : 'Erro na aprovação em massa')
    } finally {
      setBulkSaving(false)
    }
  }

  if (loading) return <p className={styles.placeholderMsg}>A carregar…</p>

  return (
    <>
      <header className={styles.sectionHeader}>
        <h1 className={styles.sectionTitle}>Feed social (moderação)</h1>
        <p className={styles.sectionSubtitle}>
          Controle o que aparece no feed social do cliente: aprovar, ocultar e destacar posts.
        </p>
      </header>
      {error && (
        <div className={styles.erroSection}>
          <p className={styles.erro}>{error}</p>
        </div>
      )}

      <div className={styles.moderationToolbar}>
        <div className={styles.formGroup}>
          <label htmlFor="social-filtro">Filtrar</label>
          <select id="social-filtro" value={filtro} onChange={(e) => setFiltro(e.target.value as Filtro)}>
            <option value="all">Todos</option>
            <option value="pendente">Pendentes</option>
            <option value="aprovado">Aprovados</option>
            <option value="oculto">Ocultos</option>
          </select>
        </div>
        {selected.size > 0 && (
          <button type="button" className={styles.btnSalvar} onClick={bulkApprove} disabled={bulkSaving}>
            {bulkSaving ? 'A aprovar…' : `Aprovar seleccionados (${selected.size})`}
          </button>
        )}
      </div>

      <div className={styles.sectionListCard}>
        {list.length === 0 ? (
          <p className={styles.listEmpty}>Nenhum post social.</p>
        ) : (
          <>
            <div className={styles.bulkSelectRow}>
              <label className={styles.checkboxInline}>
                <input
                  type="checkbox"
                  checked={selected.size === list.length && list.length > 0}
                  onChange={toggleSelectAll}
                />
                Seleccionar todos
              </label>
            </div>
            <div className={styles.listScrollArea}>
              <ul className={styles.list}>
              {list.map((item) => (
                <li key={item.id} className={styles.listItem}>
                  <input
                    type="checkbox"
                    className={styles.listItemCheckbox}
                    checked={selected.has(item.id)}
                    onChange={() => toggleSelect(item.id)}
                    aria-label={`Seleccionar post de ${item.usuario_nome}`}
                  />
                  <div className={styles.listItemContent}>
                    <div className={styles.listItemTitle}>
                      {item.usuario_nome} {item.destaque ? '⭐' : ''}
                    </div>
                    {item.texto && <div className={styles.listItemMeta}>{item.texto}</div>}
                    {item.media_url && isImageUrl(item.media_url) && (
                      <img
                        src={resolveMediaUrl(item.media_url)}
                        alt="Média do post"
                        className={styles.socialMediaPreview}
                      />
                    )}
                    {item.media_url && !isImageUrl(item.media_url) && (
                      <div className={styles.listItemMeta}>{item.media_url}</div>
                    )}
                    <div className={styles.listItemMeta}>
                      {item.aprovado ? 'Aprovado' : 'Pendente'} · {item.ativo ? 'Ativo' : 'Oculto'}
                    </div>
                  </div>
                  <div className={styles.listItemActions}>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => patch(item.id, { aprovado: !item.aprovado })}
                      disabled={savingId === item.id}
                    >
                      {item.aprovado ? 'Desaprovar' : 'Aprovar'}
                    </button>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => patch(item.id, { destaque: !item.destaque })}
                      disabled={savingId === item.id}
                    >
                      {item.destaque ? 'Tirar destaque' : 'Destacar'}
                    </button>
                    <button
                      type="button"
                      className={styles.btnDanger}
                      onClick={() => patch(item.id, { ativo: !item.ativo })}
                      disabled={savingId === item.id}
                    >
                      {item.ativo ? 'Ocultar' : 'Reativar'}
                    </button>
                  </div>
                </li>
              ))}
              </ul>
            </div>
          </>
        )}
      </div>
    </>
  )
}
